import { useEffect, useMemo, useState } from "react";
import {
  ExternalLink,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShoppingBag,
  UploadCloud,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/Button";
import { Card, SectionHeader } from "@/components/Card";
import { Modal } from "@/components/Modal";
import { MediaUpload } from "@/components/MediaUpload";
import {
  CatalogProductInput,
  WhatsappAsset,
  whatsappPlatformService as service,
} from "@/services/whatsapp-platform.service";
import { Select } from '@/components/Select';

const field =
  "mt-1 w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/15";
const fresh: CatalogProductInput = {
  name: "",
  retailerId: "",
  brand: "",
  description: "",
  price: "",
  currency: "AED",
  availability: "in stock",
  condition: "new",
  visibility: "staging",
  url: "",
  imageUrl: "",
};

function productInput(row: WhatsappAsset): CatalogProductInput {
  const d = row.data;
  const saved = d.draft ?? d.inventoryInput;
  if (saved) return { ...fresh, ...saved, version: row.inventoryVersion ?? 0 };
  // Meta returns formatted price text. Ask for an explicit amount instead of guessing its scale or decimal separator.
  return {
    ...fresh,
    name: row.name ?? d.name ?? "",
    retailerId: d.retailer_id ?? "",
    brand: d.brand ?? "",
    description: d.description ?? "",
    currency: d.currency ?? "AED",
    availability: String(d.availability ?? "in stock").replace(/_/g, " "),
    condition: d.condition ?? "new",
    visibility: d.visibility ?? "published",
    url: d.url ?? "",
    imageUrl: d.imageUrl ?? "",
    catalogId: d.catalogId,
    version: row.inventoryVersion ?? 0,
  };
}

export default function CatalogProducts({
  accountUid,
  assets,
  onChanged,
  prefill,
  onPrefillUsed,
}: {
  accountUid: string;
  assets: WhatsappAsset[];
  onChanged: () => Promise<void>;
  /** A new draft to open, e.g. from a CRM Catalog item. */
  prefill?: Partial<CatalogProductInput> | null;
  onPrefillUsed?: () => void;
}) {
  const products = assets.filter(
    (a) => a.kind === "product" && !a.data.appliedTo,
  );
  const catalogs = assets.filter(
    (a) => a.kind === "catalog" && a.metaId && !a.data.appliedTo,
  );
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<WhatsappAsset | null>(null);
  const [input, setInput] = useState<CatalogProductInput>(fresh);
  const [busy, setBusy] = useState(false);
  const shown = useMemo(
    () =>
      products.filter((p) =>
        `${p.data.draft?.name ?? p.name} ${p.data.retailer_id}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    [products, search],
  );
  const edit = (row?: WhatsappAsset) => {
    setCurrent(row ?? null);
    setInput(
      row ? productInput(row) : { ...fresh, catalogId: catalogs[0]?.metaId },
    );
    setOpen(true);
  };
  useEffect(() => {
    if (!prefill) return;
    setCurrent(null);
    setInput({ ...fresh, catalogId: catalogs[0]?.metaId, ...prefill });
    setOpen(true);
    onPrefillUsed?.();
    // Opens once per hand-off; catalogs only seed the default target catalog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);
  const set = (key: keyof CatalogProductInput, value: string) =>
    setInput((p) => ({ ...p, [key]: value }));
  const locked = current?.status === "unknown" || current?.status === "pending";
  const valid =
    input.name.trim() &&
    input.retailerId.trim() &&
    input.description.trim() &&
    input.brand.trim() &&
    Number(input.price) > 0 &&
    input.imageUrl &&
    input.url;
  const save = async (publish: boolean) => {
    if (!valid || locked) return;
    if (
      publish &&
      !window.confirm(
        current?.metaId
          ? `Apply these changes to “${input.name}” on Meta?`
          : `Create “${input.name}” in the selected Meta catalog${input.visibility === "staging" ? " as a hidden product" : " and make it visible"}?`,
      )
    )
      return;
    setBusy(true);
    try {
      const row = await service.saveProduct(
        accountUid,
        input,
        current?.assetUid,
      );
      setCurrent(row);
      setInput(productInput(row));
      if (publish) {
        const published = await service.publishProduct(
          accountUid,
          row.assetUid,
          row.inventoryVersion ?? 0,
        );
        setCurrent(published);
        setInput(productInput(published));
        toast.success("Product saved on Meta and read back successfully");
      } else toast.success("Product draft saved");
      await onChanged();
      setOpen(false);
    } catch {
      // Reload the saved draft and exact publish state after an error, including an unknown remote outcome.
      const rows = await service.list(accountUid).catch(() => []);
      const row = rows.find(
        (r) =>
          r.kind === "product" &&
          r.data.retailer_id === input.retailerId.trim(),
      );
      if (row) {
        setCurrent(row);
        setInput(productInput(row));
      }
      await onChanged();
    } finally {
      setBusy(false);
    }
  };
  const check = async () => {
    if (!current) return;
    setBusy(true);
    try {
      const row = await service.checkProduct(accountUid, current.assetUid);
      setCurrent(row);
      setInput(productInput(row));
      await onChanged();
      toast.success(
        "Product found on Meta. Review any saved changes before publishing.",
      );
    } catch {
      /* Interceptor displays the API reason. */
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <SectionHeader
        title="Product catalog"
        subtitle={`${products.length} products · ${catalogs.length ? catalogs.map((c) => c.name).join(", ") : "No catalog linked yet"}`}
        icon={<ShoppingBag size={19} />}
        action={
          <Button onClick={() => edit()} iconLeft={<Plus size={15} />}>
            Add product
          </Button>
        }
      />
      {!catalogs.length && (
        <div className="mb-4 rounded-xl border border-border bg-primary-soft p-4 text-sm">
          <p className="font-semibold">Prepare your products here</p>
          <p className="mt-1 text-text-secondary">
            Save drafts now. To publish, link a catalog to this WhatsApp
            business in Commerce Manager, grant catalog management access, then
            sync this page.
          </p>
          <a
            href="https://business.facebook.com/commerce/"
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 font-medium text-primary"
          >
            Open Commerce Manager <ExternalLink size={13} />
          </a>
        </div>
      )}
      <label className="relative mb-4 block">
        <Search size={16} className="absolute left-3 top-3.5 text-text-muted" />
        <input
          aria-label="Search products"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products or SKU…"
          className={`${field} !pl-9`}
        />
      </label>
      {shown.length ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((p) => {
            const d = p.data.draft ?? p.data.inventoryInput;
            return (
              <button
                key={p.assetUid}
                onClick={() => edit(p)}
                className="group overflow-hidden rounded-xl border border-border text-left transition hover:border-primary hover:shadow-sm"
              >
                <div className="relative flex aspect-[4/3] items-center justify-center bg-surface-alt">
                  {(d?.imageUrl ?? p.data.imageUrl) ? (
                    <img
                      src={d?.imageUrl ?? p.data.imageUrl}
                      alt={d?.name ?? p.name ?? "Product"}
                      className="h-full w-full object-contain p-3"
                    />
                  ) : (
                    <ShoppingBag size={32} className="text-text-muted" />
                  )}
                  <span className="absolute right-2 top-2 rounded-lg bg-surface px-2 py-1 text-xs capitalize text-text-secondary">
                    {p.status === "synced"
                      ? p.data.visibility === "staging"
                        ? "Hidden on Meta"
                        : "On Meta"
                      : p.status === "unknown"
                        ? "Check Meta"
                        : p.status}
                  </span>
                </div>
                <div className="space-y-1 p-3">
                  <p className="truncate text-sm font-semibold">
                    {d?.name ?? p.name}
                  </p>
                  <p className="text-xs text-text-muted">
                    {p.data.retailer_id}
                  </p>
                  <div className="flex justify-between gap-2">
                    <p className="text-sm font-semibold text-primary">
                      {d
                        ? `${d.currency} ${d.price}`
                        : `${p.data.price ?? "—"} ${p.data.currency ?? ""}`}
                    </p>
                    <Pencil
                      size={14}
                      className="text-text-muted group-hover:text-primary"
                    />
                  </div>
                  <p className="text-xs capitalize text-text-muted">
                    {String(
                      d?.availability ?? p.data.availability ?? "",
                    ).replace(/_/g, " ")}
                  </p>
                  {p.syncError && (
                    <p className="line-clamp-2 text-xs text-danger">
                      {p.syncError}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border py-14 text-center">
          <ShoppingBag className="mx-auto mb-3 text-primary" size={30} />
          <p className="font-semibold">
            {search ? "No matching products" : "Your collection starts here"}
          </p>
          <p className="mt-1 text-sm text-text-muted">
            Add an image, price and description for your first product.
          </p>
          <Button variant="secondary" className="mt-4" onClick={() => edit()}>
            Create product draft
          </Button>
        </div>
      )}
      <Modal
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        title={current ? "Edit product" : "Create product"}
        widthClass="max-w-4xl"
      >
        <div className="grid gap-6 md:grid-cols-[1fr_260px]">
          <fieldset
            disabled={busy || locked}
            className="min-w-0 space-y-4 disabled:opacity-60"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-medium text-text-secondary">
                Product name
                <input
                  className={field}
                  value={input.name}
                  maxLength={150}
                  onChange={(e) => set("name", e.target.value)}
                />
              </label>
              <label className="text-xs font-medium text-text-secondary">
                SKU / retailer ID
                <input
                  className={field}
                  value={input.retailerId}
                  maxLength={100}
                  disabled={!!current}
                  onChange={(e) => set("retailerId", e.target.value)}
                />
              </label>
            </div>
            <label className="block text-xs font-medium text-text-secondary">
              Description
              <textarea
                rows={3}
                className={field}
                maxLength={5000}
                value={input.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs font-medium text-text-secondary">
                Brand
                <input
                  className={field}
                  value={input.brand}
                  maxLength={100}
                  onChange={(e) => set("brand", e.target.value)}
                />
              </label>
              <label className="text-xs font-medium text-text-secondary">
                Price
                <input
                  className={field}
                  value={input.price}
                  inputMode="decimal"
                  placeholder="e.g. 49.00"
                  onChange={(e) => set("price", e.target.value)}
                />
              </label>
              <label className="text-xs font-medium text-text-secondary">
                Currency
                <input
                  className={field}
                  value={input.currency}
                  maxLength={3}
                  onChange={(e) =>
                    set("currency", e.target.value.toUpperCase())
                  }
                />
              </label>
            </div>
            {current?.metaId &&
              !current.data.inventoryInput &&
              !current.data.draft && (
                <p className="text-xs text-text-muted">
                  Current Meta price: {current.data.price}{" "}
                  {current.data.currency}. Enter the amount explicitly before
                  saving.
                </p>
              )}
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-text-secondary">
                Availability
                <Select
                  className={field}
                  value={input.availability}
                  onChange={(e) => set("availability", e.target.value)}
                >
                  {[
                    "in stock",
                    "out of stock",
                    "preorder",
                    "available for order",
                    "discontinued",
                  ].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </Select>
              </label>
              <label className="text-xs font-medium text-text-secondary">
                Condition
                <Select
                  className={field}
                  value={input.condition}
                  onChange={(e) => set("condition", e.target.value)}
                >
                  {["new", "refurbished", "used"].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </Select>
              </label>
            </div>
            <label className="block text-xs font-medium text-text-secondary">
              Product page URL
              <input
                type="url"
                className={field}
                value={input.url}
                placeholder="https://your-store.com/products/item"
                onChange={(e) => set("url", e.target.value)}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-text-secondary">
                Catalog
                <Select
                  className={field}
                  value={input.catalogId ?? ""}
                  disabled={!!current?.metaId}
                  onChange={(e) =>
                    setInput((p) => ({
                      ...p,
                      catalogId: e.target.value || undefined,
                    }))
                  }
                >
                  <option value="">Select after linking</option>
                  {catalogs.map((c) => (
                    <option key={c.assetUid} value={c.metaId}>
                      {c.name ?? c.metaId}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="text-xs font-medium text-text-secondary">
                Visibility on Meta
                <Select
                  className={field}
                  value={input.visibility}
                  onChange={(e) => set("visibility", e.target.value)}
                >
                  <option value="staging">Hidden</option>
                  <option value="published">Visible</option>
                </Select>
              </label>
            </div>
          </fieldset>
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-surface-alt p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
                Product image
              </p>
              <div className="mb-4 flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-surface">
                {input.imageUrl ? (
                  <img
                    src={input.imageUrl}
                    alt={input.name || "Product preview"}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <ShoppingBag size={40} className="text-primary" />
                )}
              </div>
              <fieldset disabled={busy || locked}>
                <MediaUpload
                  mode="image"
                  folder="whatsapp/catalog/images"
                  value={input.imageUrl}
                  onChange={(url, meta) =>
                    setInput((p) => ({
                      ...p,
                      imageUrl: url,
                      imageFileId: meta?.fileId,
                    }))
                  }
                />
              </fieldset>
            </div>
            <p className="text-xs leading-relaxed text-text-muted">
              Your image stays in your company’s library. Save a draft to review
              it, then publish when you are ready. Meta may review products
              before customers can see them.
            </p>
            {current?.metaId && (
              <p className="break-all text-xs text-text-muted">
                Meta product · {current.metaId}
              </p>
            )}
          </div>
        </div>
        {current?.syncError && (
          <div
            role="alert"
            className="mt-4 rounded-xl bg-danger-soft p-3 text-sm text-danger"
          >
            {current.syncError}
          </div>
        )}
        {locked && (
          <div className="mt-4 rounded-xl bg-primary-soft p-3 text-sm">
            The previous publish needs confirmation. Check Meta before editing
            or publishing again.
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
          <div className="flex flex-wrap gap-2">
            {current && (locked || current.metaId) && (
              <Button
                variant="secondary"
                loading={busy}
                onClick={check}
                iconLeft={<RefreshCw size={14} />}
              >
                Check Meta
              </Button>
            )}
            <Button
              variant="secondary"
              loading={busy}
              disabled={!valid || locked}
              onClick={() => save(false)}
            >
              Save draft
            </Button>
            <Button
              loading={busy}
              disabled={!valid || !input.catalogId || locked}
              onClick={() => save(true)}
              iconLeft={<UploadCloud size={15} />}
            >
              {current?.metaId ? "Publish changes" : "Publish product"}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
